"use client";

import React, { useMemo, useCallback } from "react";
import { ChipMultiSelect } from "@/components/ChipMultiSelect/ChipMultiSelect";
import { ChipSingleSelect } from "@/components/ChipSingleSelect/ChipSingleSelect";
import {
  BaseEntity,
  FetchEntitiesFn,
} from "@/components/EntityPickerModal/EntityPickerModal";

type FilterColor = "red" | "blue" | "green" | "purple" | "gray";

interface BaseFilterConfig<T extends BaseEntity> {
  key: string;
  label: string;
  fetchEntities: FetchEntitiesFn<T>;
  color?: FilterColor;
  entityNameSingular?: string;
  entityNamePlural?: string;
}

interface MultiFilterConfig<T extends BaseEntity> extends BaseFilterConfig<T> {
  type: "multi";
  defaultValue?: T[];
}

interface SingleFilterConfig<T extends BaseEntity> extends BaseFilterConfig<T> {
  type: "single";
  defaultValue?: T;
}

export type FilterConfig<T extends BaseEntity> =
  | MultiFilterConfig<T>
  | SingleFilterConfig<T>;

type FilterValues = Record<
  string,
  BaseEntity[] | BaseEntity | null | undefined
>;

interface ActiveFilterItem {
  id: string | number;
  name: string;
  filterKey: string;
  color: FilterColor;
  isDefault: boolean;
}

interface ChipFilterGroupProps {
  filters: FilterConfig<BaseEntity>[];
  values: FilterValues;
  onChange: (key: string, value: BaseEntity[] | BaseEntity | null) => void;
  showChips?: boolean;
  showActiveFilters?: boolean;
  onClearAll?: () => void;
}

const getFilterStyles = (color: FilterColor) => {
  switch (color) {
    case "red":
      return {
        pill: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
        button:
          "text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200",
      };
    case "blue":
      return {
        pill: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
        button:
          "text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200",
      };
    case "green":
      return {
        pill: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
        button:
          "text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200",
      };
    case "purple":
      return {
        pill: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
        button:
          "text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200",
      };
    default:
      return {
        pill: "bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300",
        button:
          "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200",
      };
  }
};

export function ChipFilterGroup({
  filters,
  values,
  onChange,
  showChips = true,
  showActiveFilters = true,
  onClearAll,
}: ChipFilterGroupProps) {
  const activeFilterItems: ActiveFilterItem[] = useMemo(() => {
    const items: ActiveFilterItem[] = [];

    filters.forEach((filter) => {
      const value = values[filter.key];
      const color = filter.color || "gray";

      if (filter.type === "multi" && Array.isArray(value)) {
        value.forEach((item) => {
          items.push({
            id: item.id,
            name: item.name,
            filterKey: filter.key,
            color,
            isDefault: false,
          });
        });
      } else if (filter.type === "single" && value && !Array.isArray(value)) {
        const isDefault =
          filter.defaultValue !== undefined &&
          filter.defaultValue.id === value.id;
        items.push({
          id: value.id,
          name: value.name,
          filterKey: filter.key,
          color,
          isDefault,
        });
      }
    });

    return items;
  }, [filters, values]);

  const hasNonDefaultFilters = useMemo(() => {
    return activeFilterItems.some((item) => !item.isDefault);
  }, [activeFilterItems]);

  const handleRemoveFilter = useCallback(
    (item: ActiveFilterItem) => {
      const filter = filters.find((f) => f.key === item.filterKey);
      if (!filter) return;

      if (filter.type === "multi") {
        const currentValue = (values[filter.key] as BaseEntity[]) || [];
        const newValue = currentValue.filter((v) => v.id !== item.id);
        onChange(filter.key, newValue);
      } else if (filter.type === "single") {
        // Reset to default value if available, otherwise null
        onChange(filter.key, filter.defaultValue || null);
      }
    },
    [filters, values, onChange],
  );

  const handleClearAll = useCallback(() => {
    if (onClearAll) {
      onClearAll();
      return;
    }

    // Default clear all behavior
    filters.forEach((filter) => {
      if (filter.type === "multi") {
        onChange(filter.key, filter.defaultValue || []);
      } else if (filter.type === "single") {
        onChange(filter.key, filter.defaultValue || null);
      }
    });
  }, [filters, onChange, onClearAll]);

  return (
    <>
      {/* Filter Chips */}
      {showChips && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {filters.map((filter) => {
            if (filter.type === "multi") {
              return (
                <ChipMultiSelect
                  entityNamePlural={filter.entityNamePlural}
                  entityNameSingular={filter.entityNameSingular}
                  fetchEntities={filter.fetchEntities}
                  key={filter.key}
                  label={filter.label}
                  onChange={(newValue) => onChange(filter.key, newValue)}
                  value={(values[filter.key] as BaseEntity[]) || []}
                />
              );
            } else {
              return (
                <ChipSingleSelect
                  entityNamePlural={filter.entityNamePlural}
                  entityNameSingular={filter.entityNameSingular}
                  fetchEntities={filter.fetchEntities}
                  key={filter.key}
                  label={filter.label}
                  onChange={(newValue) => onChange(filter.key, newValue)}
                  value={(values[filter.key] as BaseEntity) || null}
                />
              );
            }
          })}
        </div>
      )}

      {/* Active Filters */}
      {showActiveFilters && activeFilterItems.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-4 mt-4 border-t border-gray-100 dark:border-slate-700">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Active filters:
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            {activeFilterItems.map((item) => {
              const styles = getFilterStyles(item.color);
              return (
                <span
                  className={`${styles.pill} px-3 py-1 rounded-full text-sm flex items-center gap-1`}
                  key={`${item.filterKey}-${item.id}`}
                >
                  {item.name}
                  {!item.isDefault && (
                    <button
                      aria-label={`Remove ${item.name} filter`}
                      className={`ml-1 ${styles.button}`}
                      onClick={() => handleRemoveFilter(item)}
                      type="button"
                    >
                      ×
                    </button>
                  )}
                </span>
              );
            })}
            {hasNonDefaultFilters && (
              <button
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                onClick={handleClearAll}
                type="button"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
